/**
 * Avatare, Rahmen und Hotspotter-Coins (amendment 21): what a person looks like next to their
 * name, and the coins that buy the look.
 *
 * Coins are earned by reporting — the thing the map lives on — and spent on frames, round
 * animated rings around the avatar. Nothing else earns them and nothing else costs them. The
 * rules for earning keep a report worth what it was before the coins: a handful a day pays,
 * and the same cell pays once in the window the GDI itself counts one report in (brief §8).
 *
 * Pure: the catalogue, the rules, and the check that a picture is a JPEG with nothing in it but
 * the picture. A phone's photo can carry the coordinates it was taken at, and this app does not
 * keep coordinates (brief §2).
 */

import { DEDUPE_WINDOW_MS } from './gdi.ts';

/** What one report earns. */
export const COINS_PER_REPORT = 10;

/** Reports that earn coins in any 24 hours: an evening out, not a farm. */
export const PAID_REPORTS_PER_DAY = 5;

/** One paid report per cell in this long — the window §8 counts one report per person in. */
export const PAID_CELL_WINDOW_MS = DEDUPE_WINDOW_MS;

/**
 * The frames, cheapest first. A frame is a look and nothing more: it changes no number and no
 * rule anywhere in the app.
 */
export const FRAMES = [
  { key: 'neon', name: 'Neon-Puls', price: 30 },
  { key: 'rainbow', name: 'Regenbogen', price: 60 },
  { key: 'fire', name: 'Feuerring', price: 100 },
  { key: 'honey', name: 'Wabenring', price: 150 },
  { key: 'disco', name: 'Disco', price: 250 },
  { key: 'gold', name: 'Goldrausch', price: 400 },
] as const;

export type Frame = (typeof FRAMES)[number]['key'];

export function isFrame(value: unknown): value is Frame {
  return FRAMES.some((frame) => frame.key === value);
}

export function priceOf(frame: Frame): number {
  return FRAMES.find((entry) => entry.key === frame)?.price ?? Number.POSITIVE_INFINITY;
}

/**
 * Accessoires (amendment 23), cheapest first: something worn on the head or over the eyes, on
 * top of the picture and its frame. One at a time, bought with the same coins, and like a
 * frame a look and nothing more.
 */
export const ACCESSORIES = [
  { key: 'bloom', name: 'Blüte', price: 20 },
  { key: 'shades', name: 'Sonnenbrille', price: 40 },
  { key: 'party', name: 'Partyhut', price: 60 },
  { key: 'aviators', name: 'Fliegersonnenbrille', price: 80 },
  { key: 'tophat', name: 'Zylinder', price: 100 },
  { key: 'flightcap', name: 'Fliegerhut', price: 120 },
  { key: 'horns', name: 'Teufelshörner', price: 150 },
  { key: 'halo', name: 'Heiligenschein', price: 150 },
  { key: 'crown', name: 'Krone', price: 300 },
] as const;

export type Accessory = (typeof ACCESSORIES)[number]['key'];

export function isAccessory(value: unknown): value is Accessory {
  return ACCESSORIES.some((accessory) => accessory.key === value);
}

export function accessoryPriceOf(accessory: Accessory): number {
  return ACCESSORIES.find((entry) => entry.key === accessory)?.price ?? Number.POSITIVE_INFINITY;
}

/** What the app makes of a photo before it sends it: a square, this many pixels a side. */
export const AVATAR_SIDE = 256;

/** The largest picture taken, in bytes and in pixels a side: room above what the app sends. */
export const AVATAR_MAX_BYTES = 96 * 1024;
export const AVATAR_MAX_SIDE = 512;

export type Picture =
  | { ok: true; jpeg: Uint8Array; width: number; height: number }
  | { ok: false; reason: 'not_jpeg' | 'too_large' };

const SOI = 0xd8;
const EOI = 0xd9;
const SOS = 0xda;

/** Start of frame: SOF0 … SOF15, less the three codes in that range that are something else. */
function isFrameHeader(marker: number): boolean {
  return marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
}

/** What a decoder needs before the scan: quantisation and Huffman tables, restarts, the frame. */
function isNeeded(marker: number): boolean {
  return (
    marker === 0xdb ||
    marker === 0xc4 ||
    marker === 0xdd ||
    marker === 0xcc ||
    isFrameHeader(marker)
  );
}

/**
 * A JPEG as the app keeps it: every segment before the picture's scan that only carries
 * metadata — EXIF with its GPS, XMP, ICC profiles, JFIF thumbnails, comments — is gone, the
 * picture itself untouched. Anything that does not read as a JPEG from its first byte to its
 * last is refused rather than guessed at.
 *
 * The scan and what follows it are kept as they are: the app's own canvas writes nothing after
 * it, and there is nothing in it but the picture.
 */
export function sanitizeJpeg(bytes: Uint8Array): Picture {
  const notJpeg = { ok: false, reason: 'not_jpeg' } as const;
  if (bytes.length > AVATAR_MAX_BYTES) return { ok: false, reason: 'too_large' };
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== SOI) return notJpeg;
  if (bytes[bytes.length - 2] !== 0xff || bytes[bytes.length - 1] !== EOI) return notJpeg;

  const kept: Uint8Array[] = [Uint8Array.of(0xff, SOI)];
  let width = 0;
  let height = 0;
  let at = 2;
  for (;;) {
    if (bytes[at] !== 0xff) return notJpeg;
    while (bytes[at] === 0xff) at += 1; // fill bytes may pad a marker
    const marker = bytes[at];
    at += 1;
    if (marker === undefined || marker === SOI || marker === EOI) return notJpeg;
    // A restart or TEM outside a scan stands alone and means nothing here.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;

    const length = ((bytes[at] ?? 0) << 8) | (bytes[at + 1] ?? 0);
    const end = at + length;
    if (length < 2 || end > bytes.length) return notJpeg;

    if (marker === SOS) {
      if (width === 0 || height === 0) return notJpeg;
      kept.push(Uint8Array.of(0xff, SOS), bytes.subarray(at));
      break;
    }
    if (isFrameHeader(marker)) {
      if (length < 8) return notJpeg;
      height = ((bytes[at + 3] ?? 0) << 8) | (bytes[at + 4] ?? 0);
      width = ((bytes[at + 5] ?? 0) << 8) | (bytes[at + 6] ?? 0);
    }
    if (isNeeded(marker)) kept.push(Uint8Array.of(0xff, marker), bytes.subarray(at, end));
    at = end;
  }

  if (width > AVATAR_MAX_SIDE || height > AVATAR_MAX_SIDE)
    return { ok: false, reason: 'too_large' };
  const jpeg = new Uint8Array(kept.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  for (const part of kept) {
    jpeg.set(part, offset);
    offset += part.length;
  }
  return { ok: true, jpeg, width, height };
}

/**
 * How somebody looks next to their name: whether they have a picture — as the time it was
 * set, which tells a cache when to let go of it — and the frame and accessory they wear.
 */
export type Look = { avatar: number | null; frame: Frame | null; accessory: Accessory | null };
